import { useMemo, useState } from 'react';
import { isEditor, useStore } from '../store/store';
import { NewRecordDialog } from './NewRecordDialog';
import { useTranslation } from 'react-i18next';
import { Icon } from '../panels/Icon';
import { setData, useData } from './dataStore';
import { DataIcon } from './DataIcon';
import { CategoryFilters } from './CategoryFilters';
import { categoryIcon } from './categories';
import { CATEGORY_FILTERS, NO_FILTERS, activeCount, passesFilters } from './filters';
import type { DataRecord } from './types';

const MAX_RESULTS = 300;
/** Pseudo-category: every record with a change waiting for review, approval or Unity. */
export const CHANGES = '__changes';
const PENDING_ORDER = ['approved', 'proposed', 'idea', 'needs_update', 'in_unity'];

export function isPending(r: DataRecord): boolean {
  return ['idea', 'proposed', 'approved', 'needs_update'].includes(r.status) || r.syncState === 'conflict';
}

export function DataSidebar() {
  const { t } = useTranslation();
  const categories = useData((s) => s.categories);
  const records = useData((s) => s.records);
  const category = useData((s) => s.category);
  const selectedId = useData((s) => s.selectedId);
  const search = useData((s) => s.search);
  const user = useStore((s) => s.user);
  const [creating, setCreating] = useState(false);
  const cat = useData((s) => s.categories.find((c) => c.key === s.category));
  const canCreate = isEditor(user) && !!cat?.editable && !!cat.creatable && !cat.prefab;

  const counts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const r of Object.values(records)) {
      out[r.category] = (out[r.category] ?? 0) + 1;
      if (isPending(r)) out[CHANGES] = (out[CHANGES] ?? 0) + 1;
    }
    return out;
  }, [records]);

  const needle = search.trim().toLowerCase();
  // Filters apply to the category's own list, not to search results or the changes list.
  const filterDefs = (!needle && category && category !== CHANGES && CATEGORY_FILTERS[category]) || null;
  const selection = useData((s) => (category ? s.filters[category] : undefined)) ?? NO_FILTERS;
  const filtering = !!filterDefs && activeCount(filterDefs, selection) > 0;
  const list = useMemo(() => {
    const match = (r: DataRecord) =>
      r.name.toLowerCase().includes(needle) ||
      r.key.toLowerCase().includes(needle) ||
      r.assetName.toLowerCase().includes(needle);
    const changes = !needle && category === CHANGES;
    const inCategory = (r: DataRecord) =>
      r.category === category && (!filterDefs || passesFilters(r, filterDefs, selection));
    return Object.values(records)
      .filter((r) => (needle ? match(r) : changes ? isPending(r) : inCategory(r)))
      .sort(
        (a, b) =>
          (changes ? PENDING_ORDER.indexOf(a.status) - PENDING_ORDER.indexOf(b.status) : 0) ||
          a.name.localeCompare(b.name, 'sv'),
      )
      .slice(0, MAX_RESULTS);
  }, [records, category, needle, filterDefs, selection]);
  const catLabel = (key: string) =>
    t(`data.categories.${key}`, { defaultValue: categories.find((c) => c.key === key)?.label ?? key });

  return (
    <aside className="left data-left">
      <div className="search full">
        <Icon id="search" />
        <input
          value={search}
          placeholder={t('data.search')}
          onChange={(e) => setData({ search: e.target.value })}
        />
      </div>
      <nav className="cat-list">
        <button
          className={`cat changes${!needle && category === CHANGES ? ' active' : ''}`}
          onClick={() => setData({ category: CHANGES, search: '', selectedId: null })}
        >
          <Icon id="tool-history" />
          <span className="ellipsis">{t('data.changes')}</span>
          <span className={`count${counts[CHANGES] ? ' badge' : ' muted'}`}>{counts[CHANGES] ?? 0}</span>
        </button>
        {categories.map((c) => (
          <button
            key={c.key}
            className={`cat${!needle && c.key === category ? ' active' : ''}`}
            onClick={() => setData({ category: c.key, search: '', selectedId: null })}
          >
            <Icon id={categoryIcon(c.key)} />
            <span className="ellipsis">{catLabel(c.key)}</span>
            <span className="count muted">{counts[c.key] ?? 0}</span>
          </button>
        ))}
      </nav>
      <div className="group-label">
        <span>
          {needle
            ? t('data.results', { count: list.length })
            : category === CHANGES
              ? t('data.changes')
              : category && catLabel(category)}
          {filtering && category && (
            <> · {t('data.filters.showing', { shown: list.length, total: counts[category] ?? 0 })}</>
          )}
        </span>
        {!needle && category !== CHANGES && canCreate && (
          <span className="links">
            <button className="link small" onClick={() => setCreating(true)}>
              + {t('data.create.button')}
            </button>
          </span>
        )}
      </div>
      {creating && category && <NewRecordDialog category={category} onClose={() => setCreating(false)} />}
      {filterDefs && category && <CategoryFilters category={category} filters={filterDefs} />}
      <ul className="rec-list">
        {list.map((r) => (
          <li key={r.id}>
            <button
              className={`rec${r.id === selectedId ? ' active' : ''}`}
              onClick={() =>
                setData({
                  selectedId: r.id,
                  ...(category === CHANGES && !needle ? {} : { category: r.category }),
                })
              }
            >
              <DataIcon iconKey={r.icon} fallback={categoryIcon(r.category)} />
              <span className="rec-text">
                <span className="ellipsis">{r.name}</span>
                <span className="muted small ellipsis">
                  {needle || category === CHANGES
                    ? catLabel(r.category)
                    : r.key !== r.name
                      ? r.key
                      : r.assetName}
                </span>
              </span>
              {!r.guid && r.syncState !== 'missing_in_unity' && category !== CHANGES && (
                <span className="chip new">{t('data.create.newBadge')}</span>
              )}
              {category === CHANGES && !needle ? (
                <span className={`status-pill ${r.status}`}>{t(`status.${r.status}`)}</span>
              ) : (
                r.syncState &&
                r.syncState !== 'in_sync' && (
                  <span className={`dot ${r.syncState}`} title={t(`sync.${r.syncState}`)} />
                )
              )}
            </button>
          </li>
        ))}
        {list.length === 0 && <li className="muted pad">{t('top.noResults')}</li>}
      </ul>
    </aside>
  );
}
